<template>
  <div class="max-w-7xl mx-auto pb-10 relative">
    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showPaymentModal }"
    >
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Módulo de Caja</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm font-medium">Búsqueda de clientes y aplicación de pagos en tiempo real.</p>
        </div>
        <div class="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-2 rounded-2xl border border-emerald-100 dark:border-emerald-800">
          <div class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
          <span class="text-xs font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-widest">Caja Abierta</span>
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none">
        <div class="relative group">
          <Search class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-brand-500 transition-colors" />
          <input
            v-model="searchTerm"
            type="text"
            @keyup.enter="handleSearch"
            placeholder="Ingrese Nombre del Cliente, Cédula o No. de Préstamo..."
            class="w-full pl-12 pr-32 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-brand-500 rounded-2xl text-base font-bold outline-none transition-all shadow-inner dark:text-white"
          >
          <button
            @click="handleSearch"
            :disabled="isLoading"
            class="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white font-black text-xs uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-95 disabled:opacity-50"
          >
            <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin" />
            <span v-else>Buscar</span>
          </button>
        </div>
        <p class="text-[10px] text-slate-400 mt-3 font-bold uppercase tracking-tighter ml-2">Presione ENTER para realizar la búsqueda en el padrón de deudores.</p>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-[10px] text-slate-400 uppercase font-black bg-slate-50 dark:bg-slate-800/50 border-b dark:border-slate-800">
              <tr>
                <th class="px-6 py-4">Cliente / Documento</th>
                <th class="px-6 py-4">Crédito</th>
                <th class="px-6 py-4 text-right">Saldo Pendiente</th>
                <th class="px-6 py-4 text-center">Último Pago</th>
                <th class="px-6 py-4 text-right uppercase">Acción</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr v-if="isLoading">
                <td colspan="5" class="px-6 py-12 text-center text-slate-500">
                  <Loader2 class="w-8 h-8 animate-spin mx-auto mb-3 text-brand-500" />
                  Buscando en la base de datos...
                </td>
              </tr>

              <tr v-else-if="list.length === 0 && searchTerm" class="animate-fade-in">
                <td colspan="5" class="px-6 py-12 text-center">
                  <UserX class="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p class="text-slate-500 font-bold">No se encontraron créditos activos para esta búsqueda.</p>
                </td>
              </tr>

              <tr v-else-if="list.length === 0" class="animate-fade-in">
                <td colspan="5" class="px-6 py-20 text-center">
                  <Wallet class="w-16 h-16 mx-auto mb-4 text-slate-200 dark:text-slate-800" />
                  <p class="text-slate-400 font-black uppercase tracking-widest text-xs">Caja a la espera de búsqueda...</p>
                </td>
              </tr>

              <tr v-for="item in list" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-slate-500">{{ (item.customer_name || 'C').charAt(0) }}</div>
                    <div>
                      <p class="font-black text-slate-900 dark:text-white uppercase leading-none">{{ item.customer_name }}</p>
                      <p class="text-[10px] font-mono text-black mt-1"> CEDULA:{{ item.customer_doc }}</p>
                      <p class="text-[10px] font-mono text-black mt-1"> CODIGO:{{ item.customer_code }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <p class="text-[9px] font-black text-brand-600 uppercase">{{ item.code }}</p>
                  <p class="text-xs font-bold text-slate-700 dark:text-slate-300">{{ item.product_name }}</p>
                  <p class="text-[9px] font-black text-brand-600 uppercase">{{ getFrequencyLabel(item.condition_level) }}</p>
                </td>
                <td class="px-6 py-4 text-right">
                  <p class="font-mono font-black text-slate-900 dark:text-white text-base">${{ formatNum(item.remaining_balance || item.total_amount) }}</p>
                  <p class="text-[9px] text-slate-400 font-bold uppercase">Deuda Total</p>
                </td>
                <td class="px-6 py-4 text-center text-xs font-bold text-slate-500">
                  {{ item.last_payment_date || 'Sin abonos' }}
                </td>
                <td class="px-6 py-4 text-right">
                  <button
                    @click="openPayment(item)"
                    class="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-95 flex items-center gap-2 ml-auto"
                  >
                    <Banknote class="w-4 h-4" /> Recibir Pago
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <PaymentForm
      :show="showPaymentModal"
      :loanData="selectedLoan"
      @close="showPaymentModal = false"
      @success="handlePaymentSuccess"
    />
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { Search, Loader2, Banknote, User, UserX, Wallet, Activity, CreditCard } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';

import PaymentForm from './Form.vue';

const list = ref([]);
const isLoading = ref(false);
const searchTerm = ref('');
const showPaymentModal = ref(false);
const selectedLoan = ref(null);
const { showError, showSuccess } = useToast();

const formatNum = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

const handleSearch = async () => {
  if (searchTerm.value.length < 3) return showError('Ingrese al menos 3 caracteres.');

  isLoading.value = true;
  try {
    // Buscamos solo créditos DISBURSED/ACTIVE que es lo que se paga en caja
    const user = SecureStorage.getItem('llevo_user');
    if (!user?.company) return;
    const res = await apiClient.get(`/loans?company_id=${user.company}&search=${searchTerm.value}&status=ACTIVE`);
    list.value = Array.isArray(res.data) ? res.data : (res.data.data || []);

    if (list.value.length === 0) {
      showError('No se encontró deuda activa para ese criterio.');
    }
  } catch (e) {
    showError('Error en la búsqueda.');
  } finally {
    isLoading.value = false;
  }
};

const openPayment = (loan) => {
  selectedLoan.value = loan;
  showPaymentModal.value = true;
};

const handlePaymentSuccess = () => {
  showPaymentModal.value = false;
  handleSearch(); // Refrescar balance después del pago
};

const getFrequencyLabel = (level) => {
  const map = { 1: 'Diario', 2: 'Semanal', 3: 'Quincenal', 4: 'Mensual' };
  return map[Number(level)] || 'Crédito';
};
</script>